import { ReactNode, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  Archive,
  Mail,
  MessageSquare,
  Trophy,
  Users,
} from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";

import { ApiError, NetworkError, describeError } from "../../lib/errors";
import { deleteGuild } from "./api";
import type { GuildDetail } from "./types";
import styles from "./DeleteGuildDialog.module.css";

interface Props {
  projectId: string;
  guild: GuildDetail;
  onClose: () => void;
  /**
   * Called once the guild is gone from the server, with whether THIS dialog is
   * what removed it. `false` is the dead end: somebody deleted it first, so
   * there is nothing of ours to announce and the page just leaves.
   */
  onDeleted: (deletedHere: boolean) => void;
}

/**
 * The confirmation dialog for disbanding a guild.
 *
 * Unlike the player one it asks the server nothing before the DELETE: what a
 * disband takes is the same list every time, and its only number — the member
 * count — arrived with the card that opened this dialog. So there is no
 * preflight to wait for, nothing that can fail before the operator has decided,
 * and the confirmation is the only gate.
 *
 * What is typed to confirm is the guild's NAME. A guild has no second
 * identifier on the page, and the name is what the operator was looking at when
 * they decided.
 */
export function DeleteGuildDialog({
  projectId,
  guild,
  onClose,
  onDeleted,
}: Props) {
  const [typed, setTyped] = useState("");

  const mutation = useMutation({
    mutationFn: () => deleteGuild(projectId, guild.id),
    onSuccess: () => onDeleted(true),
  });

  const deleteError =
    mutation.error instanceof ApiError ? mutation.error : null;
  // A 404 means somebody else got there first — a second tab, another
  // operator, or the leader disbanding it from the game. There is nothing left
  // to confirm, so the dialog collapses into the news and one way out.
  const alreadyGone = deleteError?.status === 404;

  // Trimmed on both sides: the name is meant to be copied out of the line
  // above, and a paste that carries a trailing space is not a different guild.
  // A blank name confirms nothing — an empty field would otherwise equal it
  // and arm the button with nothing typed at all.
  const wanted = guild.name.trim();
  const confirmed = wanted !== "" && typed.trim() === wanted;
  const canDelete = confirmed && !mutation.isPending && !alreadyGone;

  const members = guild.member_count;

  let body: ReactNode;
  if (alreadyGone) {
    body = (
      <Alert variant="danger">
        This guild no longer exists — someone deleted it first.
      </Alert>
    );
  } else if (mutation.isPending) {
    body = (
      <p className={styles.modalBodyText} style={{ margin: 0 }}>
        Disbanding the guild, clearing its chat and telling the members…
      </p>
    );
  } else {
    body = (
      <>
        <p className={styles.modalBodyText}>
          The guild and everything inside it go for good.{" "}
          <b>This cannot be undone.</b>
        </p>

        <div className={styles.section}>
          <div className={styles.label}>What goes</div>
          <div className={styles.list}>
            <Row icon={<Users size={13} />}>
              {members === 1 ? (
                <>Its one member loses the guild — they keep their account</>
              ) : (
                <>
                  All <span className={styles.num}>{members}</span> members lose
                  the guild — they keep their accounts
                </>
              )}{" "}
              and may join another one right away
            </Row>
            <Row icon={<MessageSquare size={13} />}>
              The whole chat history
            </Row>
            {/* The donations are named because they are the one loss here
                that lands on somebody who did nothing: help_requests cascade
                with the guild whatever their status, and help_donations
                cascade with them, so a player who gave into a request loses
                what the recipient had not collected yet. The player-deletion
                dialog names unclaimed donations for the same reason. */}
            <Row icon={<Mail size={13} />}>
              Help requests with the donations still sitting unclaimed in
              members&apos; inboxes, pending invites, join requests and the
              block list
            </Row>
            <Row icon={<Trophy size={13} />}>
              Its place in every leaderboard cycle still running
            </Row>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.label}>Kept</div>
          <div className={styles.list}>
            <Row muted icon={<Archive size={13} />}>
              Closed leaderboard cycles and the rewards already paid out — the
              archive is not rewritten
            </Row>
          </div>
        </div>

        {/* The realtime promise, kept honest: the disband reaches whoever is
            connected, and the reconnect buffer is a minute wide. Nobody is
            told that everyone will hear. */}
        <div className={styles.section}>
          <Alert variant="warn">
            Members who are online right now are told immediately; the rest find
            out the next time the game asks for the guild.
          </Alert>
        </div>

        <div className={styles.confirm}>
          {wanted === "" ? (
            // Nothing to retype, so there is no gate to pass — and a gate that
            // passes itself is worse than none: the button would arm on an
            // empty field. The game lets a guild be named nothing at all
            // (POST /v1/guilds only refuses "", PATCH /v1/guilds/{id} checks
            // nothing), so this says what to do about it.
            <span className={styles.helper}>
              This guild has no name, and the deletion is confirmed by typing
              it. Give it one in the guild&apos;s settings first, then delete.
            </span>
          ) : (
            <>
              {/* A <span>, not a <label htmlFor>: Chrome's label activation
                  fires on mouseup inside a label and collapses the selection
                  of the very name that is here to be copied. `aria-label`
                  names the field instead — as in DeleteProjectModal,
                  RotateConfirmModal and ApplyConfirmModal. */}
              <span className={styles.modalLabel}>
                Type <code>{guild.name}</code> to confirm
              </span>
              <input
                className={styles.input}
                aria-label={`Type ${guild.name} to confirm`}
                autoComplete="off"
                spellCheck={false}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
              <span className={styles.helper}>
                The guild&apos;s name, exactly as it is above.
              </span>
            </>
          )}
        </div>

        {/* Every failure says something, and what it can honestly say depends
            on which one it is. The one thing this dialog must never do is put
            the form back exactly as it was — that reads as "the button didn't
            work" on the most destructive operation in the panel, and the
            answer to that is to press it again.

            The NetworkBanner now raises itself on the same failure, and this
            plaque still earns its place: the banner says the API is out of
            reach, which is not the question in front of the operator. The
            question is what became of THIS guild, and only the dialog that
            asked can answer it. */}
        {mutation.isError && (
          <div className={styles.errorBlurb} role="alert">
            {deleteError ? (
              <>
                Couldn&apos;t delete the guild. Nothing was changed — the whole
                disband runs in one transaction.{" "}
                <span>{describeError(deleteError.code)}</span>
              </>
            ) : mutation.error instanceof NetworkError ? (
              // No promise about what happened: the request may have been
              // carried out and only the answer lost.
              <>
                Couldn&apos;t reach Social Hub API, so there is no telling
                whether the guild went. Check the connection and try again — if
                it did go, the next attempt says so.
              </>
            ) : (
              // Neither the server's answer nor the transport — something
              // inside the panel broke. Naming the connection here would be a
              // guess, and this failure can even follow a delete that worked.
              <>
                Couldn&apos;t delete the guild, and the failure says nothing
                about why — there is no telling whether it went. Reload the
                list to check.
              </>
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <Modal
      open
      // In the dead end there is nothing to close back TO: the card behind
      // this dialog describes a guild that no longer exists, and dismissing
      // onto it leaves the operator reading a stale page. So the ×, Escape and
      // the backdrop all do what the single button does — leave for the list.
      // `closeDisabled` is not the tool for this: Modal documents it as "a
      // request this dialog started is in flight", and here nothing is.
      onClose={alreadyGone ? () => onDeleted(false) : onClose}
      // While the delete is in flight there is no way out of the dialog, and
      // it says so: the × greys out, Escape and the backdrop do nothing. The
      // operation is not idempotent from the operator's side, so a second
      // attempt is not something to leave one keystroke away.
      closeDisabled={mutation.isPending}
      title={wanted === "" ? "Delete this guild?" : `Delete ${guild.name}?`}
      // The header says what kind of question this is before the body says
      // what it is about. The × stays: greyed out while the delete is in
      // flight, live at every other moment, because a dialog whose only exit
      // is a footer button is a dialog you can lose your way out of.
      icon={<AlertTriangle size={17} />}
      iconTone="danger"
      width="sm"
      footer={
        alreadyGone ? (
          <Button variant="primary" onClick={() => onDeleted(false)}>
            Back to guilds
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => mutation.mutate()}
              disabled={!canDelete}
              loading={mutation.isPending}
            >
              {mutation.isPending ? "Deleting…" : "Delete guild"}
            </Button>
          </>
        )
      }
    >
      {body}
    </Modal>
  );
}

function Row({
  icon,
  muted,
  children,
}: {
  icon: ReactNode;
  muted?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`${styles.item} ${muted ? styles.muted : ""}`}>
      <span className={styles.ico} aria-hidden>
        {icon}
      </span>
      <span>{children}</span>
    </div>
  );
}
